import {
  ComparisonRow,
  ComparisonWidget,
  GroupByDimension,
} from '../services/comparisonDashboardService';

export type ComparisonMetricTotals = Omit<ComparisonRow, 'row_key' | 'row_label'>;

/** Avg Sale / Avg Price are SQL averages of lines, not rollups of child avgs. Hide on the group list. */
export const ROLLUP_HIDDEN_METRIC_KEYS = ['avg_sale', 'avg_price'] as const;

/** Brand / category / sales rep / territory can be NULL (no product match or blank invoice field). */
export const UNASSIGNED_GROUP_LABEL = '(Unassigned)';

export function display_group_label(key: string, label: string): string {
  return label.trim() || key.trim() || UNASSIGNED_GROUP_LABEL;
}

/** Child rows under a group. Product is the only group whose children are customers. */
export function comparison_secondary_dimension(group_by: GroupByDimension): GroupByDimension {
  return group_by === 'product' ? 'customer' : 'product';
}

/**
 * Item # on Breakdown.
 * Product keys are catalog product codes (same code the executive dashboard shows).
 * Customer keys are customer numbers. Sales rep keys are rep ids.
 * Category, brand, and territory keys are the name itself, so they are not item numbers.
 */
export function breakdown_item_number(dimension: GroupByDimension, row_key: string): string {
  if (dimension === 'product' || dimension === 'customer' || dimension === 'sales_rep') {
    return row_key.trim();
  }
  return '';
}

/** Roll child rows up to one group-level metric set. Averages are left blank (not shown). */
export function aggregate_comparison_rows(rows: ComparisonRow[]): ComparisonMetricTotals {
  if (rows.length === 0) {
    return {
      revenue: 0,
      qty: 0,
      avg_sale: 0,
      min_sale: 0,
      max_sale: 0,
      avg_price: 0,
      min_price: 0,
      max_price: 0,
    };
  }

  return {
    revenue: rows.reduce((sum, row) => sum + row.revenue, 0),
    qty: rows.reduce((sum, row) => sum + row.qty, 0),
    avg_sale: 0,
    min_sale: Math.min(...rows.map((row) => row.min_sale)),
    max_sale: Math.max(...rows.map((row) => row.max_sale)),
    avg_price: 0,
    min_price: Math.min(...rows.map((row) => row.min_price)),
    max_price: Math.max(...rows.map((row) => row.max_price)),
  };
}

/** One table row per group widget; metrics summed from that group’s children. */
export function group_list_rows(widgets: ComparisonWidget[]): ComparisonRow[] {
  return widgets.map((widget) => ({
    row_key: widget.group_key,
    row_label: display_group_label(widget.group_key, widget.group_label),
    ...aggregate_comparison_rows(widget.rows),
  }));
}

export function rows_for_group(widgets: ComparisonWidget[], group_key: string): ComparisonRow[] {
  const widget = widgets.find((w) => w.group_key === group_key);
  return widget?.rows ?? [];
}

export function drill_first_column_label(
  drilled_key: string | null,
  group_label: string,
  secondary_label: string
): string {
  return drilled_key ? secondary_label : group_label;
}

export function category_drill_column_label(drilled_category_key: string | null): string {
  return drill_first_column_label(drilled_category_key, 'Category', 'Product');
}

export function widget_for_key(
  widgets: ComparisonWidget[],
  group_key: string | null
): ComparisonWidget | undefined {
  if (group_key == null) return undefined;
  return widgets.find((w) => w.group_key === group_key);
}
